<!-- Full-screen client selection overlay (service providers without a selected client) -->
<div class="client-selection-overlay" *ngIf="accountInfo?.isServiceProvider && !selectedClient && !hasSelectedClient">
    <div class="client-selection-modal">
        <div class="client-selection-header">
            <i class="material-icons">business</i>
            <h2>Select a Client</h2>
            <p>Please select a client to start using the system</p>
        </div>

        <div class="client-selection-content">
            <!-- Custom dropdown replacing mat-select -->
            <div class="overlay-custom-dropdown" [class.open]="isOverlayDropdownOpen"
                 (click)="$event.stopPropagation()">
                <button class="custom-dropdown-trigger" (click)="toggleOverlayDropdown($event)">
                    <i class="material-icons">business</i>
                    <span>{{ getClientName(tempSelectedClient) || 'Select a client...' }}</span>
                    <i class="material-icons dd-arrow" [class.rotated]="isOverlayDropdownOpen">keyboard_arrow_down</i>
                </button>
                <div class="custom-dropdown-panel" *ngIf="isOverlayDropdownOpen">
                    <div class="dropdown-search">
                        <i class="material-icons">search</i>
                        <input [(ngModel)]="clientSearchText" (ngModelChange)="filterClients()"
                               (click)="$event.stopPropagation()" placeholder="Search clients..."
                               class="dropdown-search-input">
                    </div>
                    <div class="dropdown-options">
                        <div class="dropdown-option"
                             *ngFor="let client of filteredClients"
                             [class.selected]="client.id === tempSelectedClient"
                             (click)="selectOverlayClient(client.id)">
                            <span class="option-name">{{ client.name }}</span>
                            <span class="option-type">{{ client.type }}</span>
                        </div>
                        <div class="dropdown-empty" *ngIf="filteredClients.length === 0">
                            <em>No clients found</em>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="client-selection-actions">
            <button class="btn btn-primary" [disabled]="!tempSelectedClient" (click)="confirmClientSelection()">
                <i class="material-icons" style="font-size: 18px; vertical-align: middle; margin-right: 8px;">check</i>
                Continue
            </button>
        </div>
    </div>
</div>

<!-- Loading Overlay for Client Switch -->
<div class="client-switching-overlay" *ngIf="isSwitchingClient">
    <div class="loading-content">
        <div class="spinner"></div>
        <h3>Switching Client...</h3>
        <p>Please wait while we load your client data</p>
    </div>
</div>

<div class="sidebar-container">
    <div class="plan-expired-overlay" *ngIf="initialData?.status === 'expired'">
        <p>Your current subscription has expired. To maintain uninterrupted access to our services, please renew your
            plan at your earliest convenience.</p>
        <button class="btn btn-danger" *ngIf="accountInfo?.isSuperUser && !accountInfo?.isServiceProviderClient"
            (click)="renewPlan()">Renew Now</button>
    </div>
    <div class="plan-expired-overlay" *ngIf="isOverdue(initialData?.current_bill?.datepromised)">
        <p class="text-center fw-semibold">
            <strong>⚠️ You have an overdue bill.</strong> Please settle your payment to continue using the system
            without interruption.
        </p>
        <div class="text-center mt-3" *ngIf="accountInfo?.isSuperUser && !accountInfo?.isServiceProviderClient">
            <button class="btn btn-danger px-4" (click)="renewPlan()">Settle Now</button>
        </div>
    </div>

    <!-- Compact header: logo only -->
    <div class="sidebar-header">
        <div class="logo-area">
            <a href="#">
                <img [class]="logoClass" [src]="logoSidebar" [alt]="logoAlt">
            </a>
        </div>
    </div>

    <!-- Favorites + Theme quick actions (own row below the header) -->
    <div class="sidebar-quick-actions">
        <button class="quick-action-btn"
                [class.active]="showFavoritesOnly"
                (click)="toggleFavoritesMode()">
            <i class="material-icons">{{ showFavoritesOnly ? 'star' : 'star_border' }}</i>
            <span>Favorites</span>
        </button>
        <button class="quick-action-btn"
                [class.active]="showThemePicker"
                (click)="toggleThemePicker($event)">
            <i class="material-icons">palette</i>
            <span>Theme</span>
        </button>
    </div>

    <!-- Theme color picker -->
    <div class="theme-picker" *ngIf="showThemePicker" (click)="$event.stopPropagation()">
        <span class="theme-picker-label">Theme</span>
        <div class="theme-swatches">
            <button *ngFor="let theme of themes"
                    class="theme-swatch"
                    [class.active]="currentThemeId === theme.id"
                    [style.background]="theme.primary"
                    [title]="theme.name"
                    (click)="applyTheme(theme.id)">
                <i class="material-icons" *ngIf="currentThemeId === theme.id">check</i>
            </button>
        </div>
    </div>

    <div class="sidebar-wrapper" id="sidebar" #sidebarWrapper>

        <!-- Go back button (sub-service-provider) -->
        <div class="go-back-container" *ngIf="hasPreviousAccount() && isSubServiceProvider">
            <button class="go-back-btn" (click)="goBackToPreviousAccount()" [disabled]="isSwitchingClient">
                <i class="material-icons">arrow_back</i>
                <span>Go Back to Previous Account</span>
            </button>
        </div>

        <!-- Client selector + add client -->
        <div *ngIf="!isSubServiceProvider && !isLoggedInAsSPClient">
            <!-- Custom client dropdown replacing mat-select -->
            <div class="custom-dropdown"
                 *ngIf="accountInfo?.isServiceProvider || accountInfo?.isSubServiceProvider || accountInfo?.isServiceProviderClient"
                 [class.open]="isClientDropdownOpen"
                 [class.disabled]="isSwitchingClient"
                 (click)="$event.stopPropagation()">
                <button class="custom-dropdown-trigger"
                        [disabled]="isSwitchingClient"
                        (click)="toggleClientDropdown($event)">
                    <i class="material-icons">business</i>
                    <span>{{ getClientName(selectedClient) || 'Select Client' }}</span>
                    <i class="material-icons dd-arrow" [class.rotated]="isClientDropdownOpen">keyboard_arrow_down</i>
                </button>
                <div class="custom-dropdown-panel" *ngIf="isClientDropdownOpen">
                    <div class="dropdown-search">
                        <i class="material-icons">search</i>
                        <input #clientSearchInput
                               [(ngModel)]="clientSearchText"
                               (ngModelChange)="filterClients()"
                               (click)="$event.stopPropagation()"
                               placeholder="Search clients..."
                               class="dropdown-search-input">
                    </div>
                    <div class="dropdown-options">
                        <div class="dropdown-option"
                             *ngFor="let client of filteredClients"
                             [class.selected]="client.id === selectedClient"
                             (click)="selectClientFromDropdown(client.id)">
                            <span class="option-name">{{ client.name }}</span>
                            <span class="option-type">{{ client.type }}</span>
                        </div>
                        <div class="dropdown-empty" *ngIf="filteredClients.length === 0">
                            <em>No clients found</em>
                        </div>
                    </div>
                </div>
            </div>

            <div class="add-client-container" *ngIf="accountInfo?.isServiceProvider && accountInfo?.isSuperUser">
                <button class="add-client-btn" (click)="navigateToClientRegistration()">
                    <i class="material-icons">add_business</i>
                    <span>Add New Client</span>
                </button>
            </div>
        </div>

        <!-- Plain search input replacing mat-form-field -->
        <div class="search-field">
            <i class="material-icons">search</i>
            <input [(ngModel)]="searchText" placeholder="Search..." class="search-input">
        </div>

        <!-- Favorites mode indicator -->
        <div class="favorites-mode-bar" *ngIf="showFavoritesOnly">
            <i class="material-icons">star</i>
            <span>Favorites</span>
        </div>

        <ul class="nav">
            <!-- Main standalone items -->
            <ng-container *ngFor="let menuItem of getFilteredMainItems()">
                <li routerLinkActive="active" class="{{menuItem.class}} nav-item">
                    <a [routerLink]="[menuItem.path]">
                        <i class="material-icons">{{menuItem.icon}}</i>
                        <p>{{menuItem.title}}</p>
                    </a>
                    <button class="fav-btn"
                            [class.favorited]="isFavorite(menuItem.title)"
                            (click)="toggleFavorite(menuItem.title, $event)"
                            [title]="isFavorite(menuItem.title) ? 'Remove from favorites' : 'Add to favorites'">
                        <i class="material-icons">{{ isFavorite(menuItem.title) ? 'star' : 'star_border' }}</i>
                    </button>
                </li>
            </ng-container>

            <!-- My Payroll submenu (non-superuser) -->
            <li class="nav-item has-submenu"
                *ngIf="(!accountInfo?.isSuperUser || accountInfo?.supervisorMode) && getFilteredSubmenuItemsDefault(payrollSubmenuItems).length > 0"
                (click)="togglePayrollConfiguration()">
                <a class="nav-link-submenu" id="payroll-expense">
                    <i class="material-icons">payments</i>
                    <p class="mb-0">My Payroll
                        <i class="material-icons arrow-icon" [ngClass]="{ 'arrow-rotate': isPayrollOpenConfig }">keyboard_arrow_down</i>
                    </p>
                </a>
                <ul class="submenu" [@expandCollapse]="isPayrollOpenConfig ? 'expanded' : 'collapsed'">
                    <ng-container *ngFor="let item of getFilteredSubmenuItemsDefault(payrollSubmenuItems)">
                        <li routerLinkActive="active">
                            <a class="submenu-link" [routerLink]="[item.path]">
                                <i class="material-icons">{{ item.icon }}</i>
                                <p>{{ item.title }}</p>
                            </a>
                            <button class="fav-btn" [class.favorited]="isFavorite(item.title)"
                                    (click)="toggleFavorite(item.title, $event)">
                                <i class="material-icons">{{ isFavorite(item.title) ? 'star' : 'star_border' }}</i>
                            </button>
                        </li>
                    </ng-container>
                </ul>
            </li>

            <!-- Workflows submenu -->
            <li class="nav-item has-submenu"
                *ngIf="accountInfo?.isSuperUser && getFilteredSubmenuItems(workflowSubmenuItems).length > 0"
                (click)="toggleWorkflowConfiguration()">
                <a class="nav-link-submenu" id="workflows">
                    <i class="material-icons">account_tree</i>
                    <p class="mb-0">Workflows
                        <i class="material-icons arrow-icon" [ngClass]="{ 'arrow-rotate': isWorkflowOpenConfig }">keyboard_arrow_down</i>
                    </p>
                </a>
                <ul class="submenu" [@expandCollapse]="isWorkflowOpenConfig ? 'expanded' : 'collapsed'">
                    <ng-container *ngFor="let item of getFilteredSubmenuItems(workflowSubmenuItems)">
                        <li routerLinkActive="active">
                            <a class="submenu-link" [routerLink]="[item.path]">
                                <i class="material-icons">{{ item.icon }}</i>
                                <p>{{ item.title }}</p>
                            </a>
                            <button class="fav-btn" [class.favorited]="isFavorite(item.title)"
                                    (click)="toggleFavorite(item.title, $event)">
                                <i class="material-icons">{{ isFavorite(item.title) ? 'star' : 'star_border' }}</i>
                            </button>
                        </li>
                    </ng-container>
                </ul>
            </li>

            <!-- Payroll (superuser) submenu -->
            <li class="nav-item has-submenu"
                *ngIf="(accountInfo?.isSuperUser || accountInfo?.supervisorMode) && getFilteredSubmenuItems(payrollExpensesSubmenuItems).length > 0"
                (click)="toggleSubmenu2()">
                <a class="nav-link-submenu" id="payroll-expense">
                    <i class="material-icons">payments</i>
                    <p class="mb-0">
                        Payroll&nbsp;<span *ngIf="!accountInfo?.isSuperUser">(Employee)</span>
                        <i class="material-icons arrow-icon" [ngClass]="{ 'arrow-rotate': isSubmenuOpen2 }">keyboard_arrow_down</i>
                    </p>
                </a>
                <ul class="submenu" [@expandCollapse]="isSubmenuOpen2 ? 'expanded' : 'collapsed'">
                    <ng-container *ngFor="let item of getFilteredSubmenuItems(payrollExpensesSubmenuItems)">
                        <li routerLinkActive="active">
                            <a class="submenu-link" [routerLink]="[item.path]">
                                <i class="material-icons">{{ item.icon }}</i>
                                <p>{{ item.title }}</p>
                            </a>
                            <button class="fav-btn" [class.favorited]="isFavorite(item.title)"
                                    (click)="toggleFavorite(item.title, $event)">
                                <i class="material-icons">{{ isFavorite(item.title) ? 'star' : 'star_border' }}</i>
                            </button>
                        </li>
                    </ng-container>
                </ul>
            </li>

            <!-- Employee submenu -->
            <li class="nav-item has-submenu" *ngIf="(getFilteredSubmenuItems(employeeSubmenuItems).length > 0)"
                (click)="toggleSubmenu()">
                <a class="nav-link-submenu" id="employee">
                    <i class="material-icons">library_books</i>
                    <p class="mb-0">Employee
                        <i class="material-icons arrow-icon" [ngClass]="{'arrow-rotate': isSubmenuOpen}">keyboard_arrow_down</i>
                    </p>
                </a>
                <ul class="submenu" [@expandCollapse]="isSubmenuOpen ? 'expanded' : 'collapsed'">
                    <ng-container *ngFor="let item of getFilteredSubmenuItems(employeeSubmenuItems)">
                        <li routerLinkActive="active" (click)="confirmPassword(item.path)"
                            [routerLink]="shouldNavigateDirectly(item.path) ? [item.path] : null">
                            <a class="submenu-link">
                                <i class="material-icons">{{item.icon}}</i>
                                <p>{{item.title}}</p>
                            </a>
                            <button class="fav-btn" [class.favorited]="isFavorite(item.title)"
                                    (click)="toggleFavorite(item.title, $event)">
                                <i class="material-icons">{{ isFavorite(item.title) ? 'star' : 'star_border' }}</i>
                            </button>
                        </li>
                    </ng-container>
                </ul>
            </li>

            <!-- Payroll Setup submenu -->
            <li class="nav-item has-submenu"
                *ngIf="(accountInfo?.isSuperUser || accountInfo?.supervisorMode) && getFilteredSubmenuItems(payrollSetupSubmenuItems).length > 0"
                (click)="toggleSubmenuConfiguration()">
                <a class="nav-link-submenu" id="payroll-setup">
                    <i class="material-icons">settings</i>
                    <p class="mb-0">Payroll Setup
                        <i class="material-icons arrow-icon" [ngClass]="{ 'arrow-rotate': isSubmenuOpenConfig }">keyboard_arrow_down</i>
                    </p>
                </a>
                <ul class="submenu" [@expandCollapse]="isSubmenuOpenConfig ? 'expanded' : 'collapsed'">
                    <ng-container *ngFor="let item of getFilteredSubmenuItems(payrollSetupSubmenuItems)">
                        <li routerLinkActive="active" (click)="confirmPassword(item.path)"
                            [routerLink]="shouldNavigateDirectly(item.path) ? [item.path] : null">
                            <a class="submenu-link">
                                <i class="material-icons">{{ item.icon }}</i>
                                <p>{{ item.title }}</p>
                            </a>
                            <button class="fav-btn" [class.favorited]="isFavorite(item.title)"
                                    (click)="toggleFavorite(item.title, $event)">
                                <i class="material-icons">{{ isFavorite(item.title) ? 'star' : 'star_border' }}</i>
                            </button>
                        </li>
                    </ng-container>
                </ul>
            </li>

            <!-- Reports submenu -->
            <li class="nav-item has-submenu"
                *ngIf="(accountInfo?.isSuperUser || accountInfo?.supervisorMode) && hasAnyFavoritedReports()">
                <a class="nav-link-submenu" id="reports" (click)="toggleSubmenuReport()">
                    <i class="material-icons">summarize</i>
                    <p class="mb-0">Reports
                        <i class="material-icons arrow-icon" [ngClass]="{ 'arrow-rotate': isSubmenuOpenReport }">keyboard_arrow_down</i>
                    </p>
                </a>
                <ul class="submenu" [@expandCollapse]="isSubmenuOpenReport ? 'expanded' : 'collapsed'">

                    <li class="report-group" *ngIf="hasAnyFavoritedOperations()">
                        <div class="report-group-header" (click)="toggleOperationsReport($event)">
                            <i class="material-icons report-group-icon">trending_up</i>
                            <span>Operations</span>
                            <i class="material-icons report-group-arrow" [ngClass]="{ 'arrow-rotate': isOperationsReportOpen }">keyboard_arrow_down</i>
                        </div>
                        <ul class="report-group-items" [@expandCollapse]="isOperationsReportOpen ? 'expanded' : 'collapsed'">
                            <ng-container *ngFor="let item of getFilteredSubmenuItems(operationsReportsItems)">
                                <li routerLinkActive="active" (click)="$event.stopPropagation()">
                                    <a class="report-item-link" [routerLink]="[item.path]">
                                        <i class="material-icons">{{ item.icon }}</i>
                                        <p>{{ item.title }}</p>
                                    </a>
                                    <button class="fav-btn" [class.favorited]="isFavorite(item.title)"
                                            (click)="toggleFavorite(item.title, $event)">
                                        <i class="material-icons">{{ isFavorite(item.title) ? 'star' : 'star_border' }}</i>
                                    </button>
                                </li>
                            </ng-container>
                        </ul>
                    </li>

                    <li class="report-group" *ngIf="hasAnyFavoritedCompliance()">
                        <div class="report-group-header" (click)="toggleComplianceReport($event)">
                            <i class="material-icons report-group-icon">gavel</i>
                            <span>Compliance</span>
                            <i class="material-icons report-group-arrow" [ngClass]="{ 'arrow-rotate': isComplianceReportOpen }">keyboard_arrow_down</i>
                        </div>
                        <ul class="report-group-items" [@expandCollapse]="isComplianceReportOpen ? 'expanded' : 'collapsed'">
                            <ng-container *ngFor="let item of getFilteredSubmenuItems(complianceReportsItems)">
                                <li routerLinkActive="active" (click)="$event.stopPropagation()">
                                    <a class="report-item-link" [routerLink]="[item.path]">
                                        <i class="material-icons">{{ item.icon }}</i>
                                        <p>{{ item.title }}</p>
                                    </a>
                                    <button class="fav-btn" [class.favorited]="isFavorite(item.title)"
                                            (click)="toggleFavorite(item.title, $event)">
                                        <i class="material-icons">{{ isFavorite(item.title) ? 'star' : 'star_border' }}</i>
                                    </button>
                                </li>
                            </ng-container>
                        </ul>
                    </li>

                </ul>
            </li>

            <!-- Forms submenu (non-superuser) -->
            <li class="nav-item has-submenu"
                *ngIf="!accountInfo?.isSuperUser && getFilteredSubmenuItemsDefault(formsSubmenuItems).length > 0"
                (click)="toggleFormConfiguration()">
                <a class="nav-link-submenu" id="payroll-expense">
                    <i class="material-icons">edit_square</i>
                    <p class="mb-0">Forms
                        <i class="material-icons arrow-icon" [ngClass]="{ 'arrow-rotate': isFormOpenConfig }">keyboard_arrow_down</i>
                    </p>
                </a>
                <ul class="submenu" [@expandCollapse]="isFormOpenConfig ? 'expanded' : 'collapsed'">
                    <ng-container *ngFor="let item of getFilteredSubmenuItemsDefault(formsSubmenuItems)">
                        <li routerLinkActive="active">
                            <a class="submenu-link" [routerLink]="[item.path]">
                                <i class="material-icons">{{ item.icon }}</i>
                                <p>{{ item.title }}</p>
                            </a>
                            <button class="fav-btn" [class.favorited]="isFavorite(item.title)"
                                    (click)="toggleFavorite(item.title, $event)">
                                <i class="material-icons">{{ isFavorite(item.title) ? 'star' : 'star_border' }}</i>
                            </button>
                        </li>
                    </ng-container>
                </ul>
            </li>

            <!-- Standalone bottom items (e.g. Support Ticket) -->
            <ng-container *ngFor="let item of getFilteredSubmenuItems(standaloneNavItems)">
                <li class="nav-item" routerLinkActive="active">
                    <a [routerLink]="[item.path]" [id]="item.title === 'Support Ticket' ? 'support' : item.id">
                        <i class="material-icons">{{ item.icon }}</i>
                        <p class="mb-0">{{ item.title }}</p>
                    </a>
                    <button class="fav-btn" [class.favorited]="isFavorite(item.title)"
                            (click)="toggleFavorite(item.title, $event)">
                        <i class="material-icons">{{ isFavorite(item.title) ? 'star' : 'star_border' }}</i>
                    </button>
                </li>
            </ng-container>
        </ul>
    </div>

    <div class="footer-sidebar" *ngIf="accountInfo?.isSuperUser || accountInfo?.supervisorMode">
        <a class="remittance" *ngIf="accountInfo?.isSuperUser || accountInfo?.supervisorMode" (click)="openChatMIA()">
            <i class="material-icons remitance-icon">chat</i>
            <span>&nbsp;Broadcast Message</span>
        </a>
        <a class="biometrics" *ngIf="accountInfo?.isSuperUser"
            href="https://play.google.com/store/apps/details?id=com.paypiere.biometrics" target="_blank">
            <i class="material-icons biometrics-icon">qr_code_scanner</i>
            <span>&nbsp;Biometrics</span>
        </a>
    </div>
</div>
